"use client"

import { DESIGN_SYSTEMS, DESIGN_SYSTEM_CATALOG, type DesignSystem } from "@repo/types"
import { Check } from "lucide-react"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { useDesignSystemAdmin, useUpdateDesignSystem } from "../../hooks/api/platform/use-platform"
import { useLabels } from "../../hooks/use-labels"
import { cn } from "../../lib/utils"
import { PageHeader } from "../../ui/layout/page-header"
import { apiErrorMessage } from "../superadmin/client-detail.helpers"
import { applyDesignSystem } from "./design-system.helpers"

const PREVIEW_ROWS = [
  { espece: "Chêne pédonculé", hauteur: 18.5, etat: "Bon" },
  { espece: "Platane commun", hauteur: 24, etat: "À surveiller" },
  { espece: "Tilleul à grandes feuilles", hauteur: 15.2, etat: "Bon" },
]

/** Aperçu qui reprend les composants réels ; le style Fluent s'applique via data-ds sur le conteneur. */
function Preview({ designSystem }: { designSystem: DesignSystem }) {
  return (
    <div data-ds-preview={designSystem} className="space-y-3 rounded-md border bg-background p-3">
      <div>
        <p className="text-sm font-semibold">Titre de section</p>
        <p className="text-xs text-muted-foreground">Texte secondaire d'exemple pour juger la lisibilité.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm">Enregistrer</Button>
        <Button size="sm" variant="outline">Annuler</Button>
        <Button size="sm" variant="ghost">Plus</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Espèce</TableHead>
            <TableHead>Hauteur (m)</TableHead>
            <TableHead>État</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {PREVIEW_ROWS.map((row) => (
            <TableRow key={row.espece}>
              <TableCell>{row.espece}</TableCell>
              <TableCell>{row.hauteur}</TableCell>
              <TableCell>{row.etat}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** /superadmin/design-system : choix du système de design appliqué à toute l'interface. */
export function DesignSystemPage() {
  const { labels } = useLabels()
  const { data: current, isLoading, error } = useDesignSystemAdmin()
  const update = useUpdateDesignSystem()

  if (isLoading) return <DendromapLoader />

  const choose = async (designSystem: DesignSystem) => {
    try {
      await update.mutateAsync(designSystem)
      applyDesignSystem(designSystem)
      toast.success(`Design « ${DESIGN_SYSTEM_CATALOG[designSystem].label} » appliqué`)
    } catch (e) {
      toast.error(apiErrorMessage(e, "Enregistrement impossible"))
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.designSystem}</PageHeader.Title>
        <PageHeader.Description>
          Choisissez l'apparence de toute l'application (boutons, textes, tableaux). Le changement s'applique à tous les utilisateurs.
        </PageHeader.Description>
      </PageHeader>
      {error && <p className="text-sm text-destructive">{apiErrorMessage(error, "Chargement impossible")}</p>}
      <div className="grid gap-4 xl:grid-cols-3 md:grid-cols-2">
        {DESIGN_SYSTEMS.map((ds) => {
          const active = current === ds
          return (
            <Card key={ds} className={cn(active && "border-primary ring-1 ring-primary")}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {DESIGN_SYSTEM_CATALOG[ds].label}
                  {active && <Check className="h-5 w-5 text-primary" aria-label="Actif" />}
                </CardTitle>
                <CardDescription>{DESIGN_SYSTEM_CATALOG[ds].description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Preview designSystem={ds} />
                <Button variant={active ? "outline" : "default"} disabled={active || update.isPending} onClick={() => choose(ds)}>
                  {active ? "Design actif" : "Appliquer ce design"}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
